import { useEffect, useMemo, useRef, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import {
  AreaChart,
  Area,
  CartesianGrid,
  Cell,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
} from 'recharts'
import { Download, AlertTriangle, ArrowUpRight, UserPlus, CalendarDays, ChevronLeft, ChevronRight } from 'lucide-react'
import {
  format,
  subDays,
  parseISO,
  startOfMonth,
  endOfMonth,
  startOfWeek,
  endOfWeek,
  eachDayOfInterval,
  addMonths,
  isSameMonth,
  isAfter,
} from 'date-fns'
import { it } from 'date-fns/locale'
import { cn } from '../../lib/cn'
import { getStats, getNewsletterCount, newsletterExportUrl } from '../../lib/api/client'
import { getContentList, type ContentEntity } from '../../lib/api/content'
import { Breakdown } from '../../components/ui/Breakdown'
import type { DashboardStats } from '../../lib/api/types'

const DONUT_COLORS = ['#E1021B', '#3f3f46', '#71717a', '#a1a1aa', '#d4d4d8'] // brand red + neutral greys

// Sales-channel colors, keyed by channel name so each keeps its OWN hue no matter the
// volume/sort order (positional cycling made Parkingmycar reuse Web's red). Web = brand red;
// partners get a CVD-validated, roughly brand-aligned hue. Unknown channels fall back to neutrals.
const CHANNEL_COLORS: Record<string, string> = {
  web: '#E1021B', // Garage San Marco — brand red
  app: '#4a3aa7', // GSM app, native — violet
  // The app opened in a browser is the same product as the native app, so it keeps the same
  // hue a shade lighter. Without an entry here it fell through to the neutral fallbacks and
  // read as "some other partner", despite being one of the largest channels.
  webapp: '#8b6fd4', // GSM app, browser — light violet
  parkimeter: '#1baf7a', // aqua
  parclick: '#eb6834', // orange
  parkcloud: '#2a78d6', // blue
  parkingmycar: '#008300', // green
}
const CHANNEL_FALLBACK = ['#eda100', '#e87ba4', '#71717a', '#a1a1aa', '#d4d4d8'] // amber, magenta, greys
const channelColor = (name: string, i: number) =>
  CHANNEL_COLORS[String(name).trim().toLowerCase()] ?? CHANNEL_FALLBACK[i % CHANNEL_FALLBACK.length]

// The backend labels channels by capitalising the raw source, which produces "Web", "App" and
// "Webapp" — three near-identical words for the old site, the native app and the app in a
// browser. Spell them out instead; anything unlisted keeps the backend's own label.
const CHANNEL_LABELS: Record<string, string> = {
  web: 'Sito web',
  app: 'App',
  webapp: 'Web App',
}
const channelLabel = (name: string) =>
  CHANNEL_LABELS[String(name).trim().toLowerCase()] ?? name

// per-vehicle series colors for the trend chart — brand red leads, neutral grey for the secondary type
const SERIES_COLORS: Record<string, string> = { Auto: '#E1021B', Moto: '#71717a' }
const seriesColor = (label: string, i: number) => SERIES_COLORS[label] ?? DONUT_COLORS[i % DONUT_COLORS.length]

function preset(days: number) {
  const end = new Date()
  return { from: format(subDays(end, days - 1), 'yyyy-MM-dd'), to: format(end, 'yyyy-MM-dd') }
}

const fmtChip = (v: string) => (v ? format(parseISO(v), 'd MMM yyyy', { locale: it }) : '—')
const WEEKDAYS = ['L', 'M', 'M', 'G', 'V', 'S', 'D']

/* Custom in-app range calendar — a popover that always opens (no flaky OS date picker),
   brand-styled, with click-to-select range + hover preview. Future dates are disabled. */
function RangePicker({ from, to, onChange }: { from: string; to: string; onChange: (from: string, to: string) => void }) {
  const [open, setOpen] = useState(false)
  const [month, setMonth] = useState(() => startOfMonth(parseISO(to || from || format(new Date(), 'yyyy-MM-dd'))))
  const [pendingFrom, setPendingFrom] = useState<string | null>(null)
  const [hover, setHover] = useState<string | null>(null)
  const ref = useRef<HTMLDivElement>(null)
  const today = useMemo(() => new Date(), [])

  useEffect(() => {
    if (!open) return
    setMonth(startOfMonth(parseISO(to || from || format(today, 'yyyy-MM-dd'))))
    function onDoc(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false)
        setPendingFrom(null)
        setHover(null)
      }
    }
    document.addEventListener('mousedown', onDoc)
    return () => document.removeEventListener('mousedown', onDoc)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  const grid = useMemo(
    () =>
      eachDayOfInterval({
        start: startOfWeek(startOfMonth(month), { weekStartsOn: 1 }),
        end: endOfWeek(endOfMonth(month), { weekStartsOn: 1 }),
      }),
    [month],
  )

  function pick(day: Date) {
    if (isAfter(day, today)) return
    const d = format(day, 'yyyy-MM-dd')
    if (!pendingFrom) {
      setPendingFrom(d)
      setHover(d)
    } else {
      const [a, b] = pendingFrom <= d ? [pendingFrom, d] : [d, pendingFrom]
      onChange(a, b)
      setPendingFrom(null)
      setHover(null)
      setOpen(false)
    }
  }

  // highlight bounds: committed range, or pending-start → hovered-day preview
  const ref2 = pendingFrom ? (hover ?? pendingFrom) : null
  const lo = pendingFrom ? (pendingFrom <= ref2! ? pendingFrom : ref2!) : from
  const hi = pendingFrom ? (pendingFrom <= ref2! ? ref2! : pendingFrom) : to

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="flex items-center gap-2 rounded-lg border border-line bg-card px-3 py-[7px] text-[12.5px] font-semibold text-coal transition-colors hover:border-faint"
      >
        <CalendarDays className="h-4 w-4 text-muted" />
        <span className="tabular-nums">{fmtChip(from)}</span>
        <span className="text-muted">→</span>
        <span className="tabular-nums">{fmtChip(to)}</span>
      </button>
      {open && (
        <div className="absolute right-0 z-50 mt-2 w-[284px] rounded-xl border border-line bg-card p-3 shadow-pop">
          <div className="mb-1.5 flex items-center justify-between">
            <button type="button" onClick={() => setMonth((m) => addMonths(m, -1))} className="rounded-md p-1 text-muted transition-colors hover:bg-paper hover:text-coal">
              <ChevronLeft className="h-4 w-4" />
            </button>
            <span className="font-display text-[13px] font-bold capitalize text-coal">{format(month, 'LLLL yyyy', { locale: it })}</span>
            <button
              type="button"
              onClick={() => setMonth((m) => addMonths(m, 1))}
              disabled={isSameMonth(month, today)}
              className="rounded-md p-1 text-muted transition-colors hover:bg-paper hover:text-coal disabled:pointer-events-none disabled:opacity-30"
            >
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>
          <div className="grid grid-cols-7 text-center text-[10px] font-bold uppercase tracking-wide text-faint">
            {WEEKDAYS.map((d, i) => (
              <span key={i} className="py-1">
                {d}
              </span>
            ))}
          </div>
          <div className="grid grid-cols-7" onMouseLeave={() => pendingFrom && setHover(pendingFrom)}>
            {grid.map((day) => {
              const d = format(day, 'yyyy-MM-dd')
              const inMonth = isSameMonth(day, month)
              const future = isAfter(day, today)
              const selected = d === (pendingFrom ?? from) || (!pendingFrom && d === to)
              const inRange = !selected && !!lo && !!hi && d > lo && d < hi
              return (
                <button
                  key={d}
                  type="button"
                  disabled={future}
                  onMouseEnter={() => pendingFrom && setHover(d)}
                  onClick={() => pick(day)}
                  className={cn(
                    'flex h-9 items-center justify-center text-[12.5px] tabular-nums transition-colors',
                    inRange ? 'bg-verdigris/10' : 'rounded-md',
                    selected && 'rounded-md bg-verdigris font-bold text-white',
                    !selected && !inRange && inMonth && !future && 'text-coal hover:bg-paper',
                    !selected && !inMonth && !future && 'text-faint hover:bg-paper',
                    future && 'cursor-not-allowed text-line',
                  )}
                >
                  {format(day, 'd')}
                </button>
              )
            })}
          </div>
        </div>
      )}
    </div>
  )
}

/* ── channel badge — neutral chip, the label is the differentiator (no gold/blue/violet) ── */
function channelBadge(src: string) {
  const s = (src || '').toUpperCase()
  return { cls: 'text-slate border-line bg-paper', label: s || 'DIRETTO' }
}
/* ── status chip ── */
function statusChip(status: string) {
  const s = (status || '').toUpperCase()
  if (s === 'CANCELLED' || s === 'CANCELED') return { cls: 'bg-venetian-soft text-venetian', label: 'Annullata' }
  if (s === 'DRAFT') return { cls: 'bg-oro-soft text-oro-700', label: 'Bozza' }
  return { cls: 'bg-ok-soft text-ok', label: 'Confermata' }
}

/* ── tiny sparkline ── */
function Sparkline({ points, color }: { points: number[]; color: string }) {
  if (!points.length) return null
  const max = Math.max(...points, 1)
  const w = 64
  const h = 30
  const step = points.length > 1 ? w / (points.length - 1) : w
  const d = points.map((p, i) => `${(i * step).toFixed(1)},${(h - (p / max) * (h - 4) - 2).toFixed(1)}`).join(' ')
  return (
    <svg width={w} height={h} viewBox={`0 0 ${w} ${h}`} className="absolute right-4 top-[18px]">
      <polyline points={d} fill="none" stroke={color} strokeWidth={2} strokeLinejoin="round" strokeLinecap="round" />
    </svg>
  )
}

function Kpi({
  label,
  value,
  unit,
  sub,
  spark,
  sparkColor = '#E1021B',
}: {
  label: string
  value: string | number
  unit?: string
  sub: React.ReactNode
  spark?: number[]
  sparkColor?: string
}) {
  return (
    <div className="cv-panel relative overflow-hidden p-[20px_22px]">
      <div className="text-[11px] font-bold uppercase tracking-[1.8px] text-muted">{label}</div>
      <div className="mt-2 font-display text-[34px] font-bold leading-none tracking-[-.8px] text-coal">
        {value}
        {unit && <span className="text-[17px] tracking-normal text-muted"> {unit}</span>}
      </div>
      <div className="mt-[9px] flex items-center gap-2 text-[12.5px] text-muted">{sub}</div>
      {spark && <Sparkline points={spark} color={sparkColor} />}
    </div>
  )
}

function CardHead({ title, hint, right }: { title: string; hint?: string; right?: React.ReactNode }) {
  return (
    <div className="gv-card-h">
      <h3 className="text-[14.5px] font-semibold text-coal">{title}</h3>
      {hint && <span className="text-[12px] text-muted">{hint}</span>}
      {right && <div className="ml-auto flex items-center gap-2">{right}</div>}
    </div>
  )
}

export function DashboardPage() {
  const [range, setRange] = useState(preset(30))

  const stats = useQuery<DashboardStats>({
    queryKey: ['stats', range.from, range.to],
    queryFn: () => getStats(range.from, range.to),
  })
  const newsletter = useQuery({ queryKey: ['newsletter'], queryFn: getNewsletterCount })
  const drafts = useQuery({
    queryKey: ['draftCount'],
    queryFn: () => getContentList('reservation', { limit: 1, filter: "status = 'DRAFT'" }),
  })
  // all-time reservation count — only totalCount is used (limit: 1 keeps it cheap)
  const reservationTotal = useQuery({
    queryKey: ['reservationTotal'],
    queryFn: () => getContentList('reservation', { limit: 1 }),
  })
  const latest = useQuery({
    queryKey: ['latestReservations'],
    queryFn: () => getContentList('reservation', { limit: 6, sort: 'creationDate desc' }),
  })
  // distinct from "ultime prenotazioni": newest registered users
  const members = useQuery({
    queryKey: ['latestMembers'],
    queryFn: () => getContentList('member', { limit: 6, sort: 'creationDate desc' }),
  })

  const data = stats.data
  const total = useMemo(
    () => data?.timeline?.datasets?.reduce((s, ds) => s + ds.data.reduce((a, b) => a + b, 0), 0) ?? 0,
    [data],
  )
  // one series per vehicle type (Auto, Moto) per day — stacked so the top edge = daily total
  const seriesLabels = useMemo(() => data?.timeline?.datasets?.map((d) => d.label) ?? [], [data])
  const daily = useMemo(() => {
    if (!data?.timeline) return [] as Record<string, string | number>[]
    return data.timeline.labels.map((label, i) => {
      const row: Record<string, string | number> = { label: label.replace(/\s+/g, ' ').trim() }
      data.timeline.datasets.forEach((ds) => {
        row[ds.label] = ds.data[i] ?? 0
      })
      return row
    })
  }, [data])
  // per-series totals for the legend (e.g. "Auto 588 · Moto 14")
  const seriesTotals = useMemo(
    () => (data?.timeline?.datasets ?? []).map((d) => ({ label: d.label, total: d.data.reduce((a, b) => a + b, 0) })),
    [data],
  )
  // draw order: largest series first (underneath) so a small line/fill is never buried
  const seriesByVolume = useMemo(
    () => [...seriesTotals].sort((a, b) => b.total - a.total).map((s) => s.label),
    [seriesTotals],
  )

  const donut = useMemo(() => {
    const src = data?.bySource ?? []
    // `key` keeps the raw source so the colour stays tied to the channel itself; `name` is what
    // the legend shows. Keeping them apart means renaming a label never silently changes a hue.
    return [...src]
      .sort((a, b) => b.value - a.value)
      .map((d) => ({ key: d.label, name: channelLabel(d.label), value: d.value }))
  }, [data])
  const donutTotal = donut.reduce((s, d) => s + d.value, 0)

  const topTransport = data?.byTransport?.[0]
  // sparkline for the top vehicle = that vehicle's own daily series (real)
  const topTransportSpark = useMemo(() => {
    if (!data?.timeline || !topTransport) return [] as number[]
    const ds = data.timeline.datasets.find((d) => d.label === topTransport.label)
    return ds ? ds.data : []
  }, [data, topTransport])
  const topCountry = [...(data?.byCountry ?? [])].sort((a, b) => b.value - a.value)[0]
  const draftCount = drafts.data?.totalCount ?? 0

  return (
    <div className="min-h-full">
      <div className="w-full px-9 pb-16 pt-7">
        {/* head */}
        <div className="mb-6 flex flex-wrap items-end gap-4">
          <div>
            <h2 className="font-display text-[28px] font-bold leading-tight tracking-[-.3px] text-coal">Panoramica</h2>
            <p className="mt-1.5 max-w-[62ch] text-[13.5px] text-muted">
              Andamento delle prenotazioni — Piazzale Roma, Venezia.
            </p>
          </div>
          <div className="ml-auto flex flex-wrap items-center gap-2">
            <div className="flex items-center gap-1 rounded-lg border border-line bg-card p-1">
              {[7, 30, 90, 365].map((d) => {
                const p = preset(d)
                const active = range.from === p.from && range.to === p.to
                return (
                  <button
                    key={d}
                    onClick={() => setRange(preset(d))}
                    className={`rounded-md px-2.5 py-1 text-[12px] font-semibold transition-colors ${
                      active ? 'bg-coal text-white' : 'text-muted hover:bg-paper hover:text-coal'
                    }`}
                  >
                    {d}g
                  </button>
                )
              })}
            </div>
            {/* custom in-app range calendar (no OS-native picker) */}
            <RangePicker from={range.from} to={range.to} onChange={(from, to) => setRange({ from, to })} />
          </div>
        </div>

        {/* alert */}
        {draftCount > 0 && (
          <div className="mb-5 flex items-start gap-3.5 rounded-card border border-[#f3c6cd] bg-[#fdecee] px-[18px] py-4 text-[13.5px] text-[#9b0a17]">
            <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0" />
            <div>
              <b className="font-bold">{draftCount.toLocaleString('it-IT')} prenotazioni in bozza</b> — non ancora
              confermate. Verifica gli importi e lo stato di acquisizione.{' '}
              <Link to="/reservations" className="font-bold underline">
                Vai a Prenotazioni →
              </Link>
            </div>
          </div>
        )}

        {/* KPI row */}
        <div className="mb-[18px] grid grid-cols-1 gap-[18px] sm:grid-cols-2 lg:grid-cols-4">
          <Kpi label="Prenotazioni" value={total.toLocaleString('it-IT')} sub={<span>nel periodo selezionato</span>} />
          <Kpi
            label="Veicolo top"
            value={topTransport?.label ?? '—'}
            sub={<span>{topTransport ? `${topTransport.value.toLocaleString('it-IT')} prenotazioni` : '—'}</span>}
          />
          <Kpi
            label="Paese top"
            value={topCountry?.label ?? '—'}
            sub={<span>{topCountry ? `${topCountry.value.toLocaleString('it-IT')} prenotazioni` : '—'}</span>}
          />
          <Kpi
            label="Prenotazioni totali"
            // Show the live reservation count. Use `||` (not `??`) so a missing/0 `total` from the
            // backend falls back to the content count — and if the backend later provides a real
            // `total` (non-zero), it's used automatically.
            value={(data?.total || reservationTotal.data?.totalCount)?.toLocaleString('it-IT') ?? '…'}
            sub={<span>in archivio</span>}
          />
        </div>

        {/* bar chart + donut */}
        <div className="mb-[18px] grid grid-cols-1 gap-[18px] lg:grid-cols-[1.8fr_1fr]">
          <div className="cv-panel">
            <CardHead title="Andamento prenotazioni" hint="per giorno nel periodo" />
            <div className="p-5">
              <div className="mb-3 flex flex-wrap items-center gap-x-5 gap-y-1.5">
                {seriesTotals.map((s, i) => (
                  <span key={s.label} className="flex items-center gap-2 text-[12.5px]">
                    <i className="h-[3px] w-4 rounded-full" style={{ background: seriesColor(s.label, i) }} />
                    <span className="font-semibold text-coal">{s.label}</span>
                    <span className="font-mono text-muted">{s.total.toLocaleString('it-IT')}</span>
                  </span>
                ))}
              </div>
              <div className="h-[200px] w-full">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={daily} margin={{ top: 6, right: 6, bottom: 0, left: -22 }}>
                    <defs>
                      {seriesLabels.map((lbl, i) => (
                        <linearGradient key={lbl} id={`area-${i}`} x1="0" y1="0" x2="0" y2="1">
                          <stop offset="0%" stopColor={seriesColor(lbl, i)} stopOpacity={0.26} />
                          <stop offset="100%" stopColor={seriesColor(lbl, i)} stopOpacity={0.02} />
                        </linearGradient>
                      ))}
                    </defs>
                    <CartesianGrid vertical={false} stroke="#f1f1f3" />
                    <XAxis dataKey="label" tickLine={false} axisLine={{ stroke: '#e4e4e7' }} interval="preserveStartEnd" minTickGap={28} tick={{ fontSize: 10, fill: '#71717a' }} />
                    <YAxis tickLine={false} axisLine={false} allowDecimals={false} width={42} tick={{ fontSize: 10, fill: '#71717a' }} />
                    <Tooltip cursor={{ stroke: '#e4e4e7' }} contentStyle={{ borderRadius: 10, border: '1px solid #e4e4e7', fontSize: 12, boxShadow: 'none', background: '#ffffff' }} />
                    {seriesByVolume.map((lbl) => {
                      const i = seriesLabels.indexOf(lbl)
                      return (
                      <Area
                        key={lbl}
                        type="monotone"
                        dataKey={lbl}
                        stroke={seriesColor(lbl, i)}
                        strokeWidth={2}
                        fill={`url(#area-${i})`}
                        dot={false}
                        activeDot={{ r: 4 }}
                        isAnimationActive={false}
                      />
                      )
                    })}
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>

          <div className="cv-panel">
            <CardHead title="Canali di vendita" />
            <div className="flex items-center gap-5 p-5">
              <div className="relative h-[130px] w-[130px] shrink-0">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie data={donut} dataKey="value" innerRadius={42} outerRadius={62} paddingAngle={2} stroke="none" isAnimationActive={false}>
                      {donut.map((d, i) => (
                        <Cell key={i} fill={channelColor(d.key, i)} />
                      ))}
                    </Pie>
                  </PieChart>
                </ResponsiveContainer>
                <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center">
                  <span className="font-display text-[18px] font-bold leading-none text-coal">{donutTotal.toLocaleString('it-IT')}</span>
                  <span className="text-[8px] font-semibold uppercase tracking-wider text-muted">prenotazioni</span>
                </div>
              </div>
              <div className="flex-1 space-y-2.5">
                {donut.map((d, i) => (
                  <div key={d.name} className="flex items-center gap-2.5 text-[13px]">
                    <span className="h-[11px] w-[11px] rounded-[3px]" style={{ background: channelColor(d.key, i) }} />
                    <span className="truncate text-slate">{d.name}</span>
                    <span className="ml-auto font-mono text-[12.5px] text-muted">
                      {donutTotal ? Math.round((d.value / donutTotal) * 100) : 0}%
                    </span>
                  </div>
                ))}
                {donut.length === 0 && <p className="text-[12.5px] text-muted">Nessun dato nel periodo.</p>}
              </div>
            </div>
          </div>
        </div>

        {/* breakdowns (all dynamic from /stats) — items-start so each card's height follows its own content */}
        <div className="mb-[18px] grid grid-cols-1 items-start gap-[18px] md:grid-cols-2 xl:grid-cols-3">
          <Breakdown title="Origine prenotazione" data={data?.bySource ?? []} />
          <Breakdown title="Metodo di pagamento" data={data?.byPaymentMode ?? []} />
          <Breakdown title="Tipologia di veicolo" data={data?.byTransport ?? []} />
          <Breakdown title="Tipologia di tariffa" data={data?.byRate ?? []} />
          <Breakdown title="Durata della sosta" data={data?.byDuration ?? []} />
          <Breakdown title="Paese" data={data?.byCountry ?? []} />
        </div>

        {/* contatti */}
        <div className="cv-panel mb-[18px] flex flex-wrap items-center justify-between gap-3 p-5">
          <div>
            <p className="text-[13px] font-semibold uppercase tracking-wide text-muted">Contatti</p>
            <p className="mt-1 text-[13px] text-slate">
              <span className="align-middle font-display text-2xl font-bold tnum text-coal">
                {newsletter.data?.toLocaleString('it-IT') ?? '…'}
              </span>{' '}
              contatti con consenso all&apos;invio di comunicazioni
            </p>
          </div>
          <a
            href={newsletterExportUrl()}
            className="inline-flex items-center gap-2 rounded-lg border border-line bg-card px-4 py-[10px] text-[13.5px] font-semibold text-slate hover:border-faint"
          >
            <Download className="h-4 w-4" /> Esporta
          </a>
        </div>

        {/* latest reservations + activity */}
        <div className="grid grid-cols-1 gap-[18px] lg:grid-cols-[1.8fr_1fr]">
          <div className="cv-panel overflow-hidden">
            <CardHead
              title="Ultime prenotazioni"
              right={
                <Link to="/reservations" className="flex items-center gap-1 text-[12.5px] font-bold text-verdigris">
                  Vedi tutte <ArrowUpRight className="h-3.5 w-3.5" />
                </Link>
              }
            />
            <table className="w-full text-[13.5px]">
              <thead>
                <tr className="bg-card-2">
                  {['Cliente', 'Periodo', 'Canale', 'Veicolo', 'Stato', 'Totale'].map((h) => (
                    <th key={h} className={`whitespace-nowrap px-4 py-[11px] text-[11px] font-bold uppercase tracking-[1.4px] text-muted ${h === 'Totale' ? 'text-right' : 'text-left'}`}>
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {(latest.data?.data ?? []).map((r) => <LatestRow key={r.id} r={r} />)}
                {latest.isLoading &&
                  Array.from({ length: 5 }).map((_, i) => (
                    <tr key={i} className="border-b border-line-soft last:border-0">
                      <td colSpan={6} className="px-4 py-3">
                        <div className="skeleton h-4 w-full rounded bg-line-soft" />
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>

          <div className="cv-panel">
            <CardHead
              title="Nuovi utenti registrati"
              right={
                <Link to="/members" className="flex items-center gap-1 text-[12.5px] font-bold text-verdigris">
                  Vedi tutti <ArrowUpRight className="h-3.5 w-3.5" />
                </Link>
              }
            />
            <div className="p-5">
              <div className="relative pl-[22px] before:absolute before:bottom-1.5 before:left-1.5 before:top-1.5 before:w-0.5 before:bg-line">
                {(members.data?.data ?? []).slice(0, 6).map((r) => {
                  const el = r.elements as Record<string, unknown>
                  const via = el.facebookID ? 'Facebook' : el.googleID ? 'Google' : 'Email'
                  return (
                    <div key={r.id} className="relative pb-[18px] last:pb-0">
                      <span className="absolute -left-[21px] top-1 h-[10px] w-[10px] rounded-full border-[2.5px] border-verdigris bg-card" />
                      <div className="text-[13.5px] font-semibold text-coal">
                        {String(el.name || '')} {String(el.lastName || '')}
                      </div>
                      <div className="truncate text-[12px] text-muted">{String(el.contactEmail || el.username || '')}</div>
                      <div className="flex items-center gap-1.5 font-mono text-[11px] text-faint">
                        <UserPlus className="h-3 w-3" /> registrato via {via}
                        {fmtEntityTime(r.creationDate)}
                      </div>
                    </div>
                  )
                })}
                {members.isLoading && <p className="text-[12.5px] text-muted">Caricamento…</p>}
                {!members.isLoading && (members.data?.data?.length ?? 0) === 0 && (
                  <p className="text-[12.5px] text-muted">Nessun utente recente.</p>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

// Yep dates are ISO strings ("2026-06-05T12:24:39Z"); some models send epoch numbers — handle both.
function fmtEntityTime(v?: number | string): string {
  if (v == null || v === '') return ''
  if (typeof v === 'number') {
    const ms = v < 1e12 ? v * 1000 : v
    try {
      return ' · ' + format(new Date(ms), 'dd/MM/yyyy')
    } catch {
      return ''
    }
  }
  const m = String(v).match(/^(\d{4})-(\d{2})-(\d{2})/)
  return m ? ` · ${m[3]}/${m[2]}/${m[1]}` : ''
}
function fmtDay(v: unknown): string {
  if (!v || typeof v !== 'string') return '—'
  try {
    return format(parseISO(v), 'dd MMM')
  } catch {
    return '—'
  }
}
function money(c: unknown): string {
  const n = Number(c)
  return Number.isFinite(n) ? new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR' }).format(n / 100) : '—'
}
function ref(o: unknown): string {
  if (o == null || typeof o !== 'object') return ''
  const r = o as { elements?: Record<string, unknown> }
  const e = r.elements ?? {}
  return String(e.label ?? e.name ?? '')
}

function LatestRow({ r }: { r: ContentEntity }) {
  const el = r.elements as Record<string, unknown>
  const ch = channelBadge(String(el.source ?? ''))
  const st = statusChip(String(el.status ?? ''))
  return (
    <tr className="border-b border-line-soft last:border-0 hover:bg-paper">
      <td className="px-4 py-3">
        <div className="font-semibold text-coal">
          {String(el.name ?? '')} {String(el.lastName ?? '')}
        </div>
        <div className="mt-0.5 text-[12px] text-muted">
          {String(el.country ?? '').toUpperCase()} · <span className="font-mono">{String(el.code ?? '')}</span>
        </div>
      </td>
      <td className="whitespace-nowrap px-4 py-3 font-mono text-[12.5px] text-slate">
        {fmtDay(el.arrivalDate)} → {fmtDay(el.leaveDate)}
      </td>
      <td className="px-4 py-3">
        <span className={`gv-src ${ch.cls}`}>{ch.label}</span>
      </td>
      <td className="px-4 py-3">
        <span className="rounded-md border border-line bg-paper px-2 py-0.5 font-mono text-[12px] font-semibold tracking-wider text-coal">
          {String(el.licencePlate ?? '—')}
        </span>
      </td>
      <td className="px-4 py-3">
        <span className={`gv-chip ${st.cls}`}>
          <i className="h-1.5 w-1.5 rounded-full bg-current" />
          {st.label}
        </span>
      </td>
      <td className="whitespace-nowrap px-4 py-3 text-right font-mono text-[13px] text-coal">{money(el.totalPrice)}</td>
    </tr>
  )
}
